import React from "react";
import { Modal } from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import {
  IconUser,
  IconMapPin,
  IconCalendar,
  IconEye,
  IconHome,
  IconSpeakerphone,
  IconHeart,
  IconBrandWhatsapp,
} from "@tabler/icons-react";

import { PropertyService } from "@/services";
import { isVideoFile } from "@/lib/media";

interface PropertyInsightModalProps {
  opened: boolean;
  onClose: () => void;
  propertyId: string;
}

export const PropertyInsightModal: React.FC<PropertyInsightModalProps> = ({
  opened,
  onClose,
  propertyId,
}) => {
  const {
    data: property,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["property-insights", propertyId],
    queryFn: async () => {
      const response = await PropertyService.GetPropertyById({
        id: propertyId,
      });

      return response?.data?.data;
    },
    enabled: !!propertyId && opened,
  });

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={<div className="text-lg font-semibold">Insights Overview</div>}
      size="xl"
      centered
      radius="md"
    >
      {isLoading && (
        <div className="flex justify-center py-10">
          <div className="animate-spin h-6 w-6 border-2 border-gray-300 border-t-blue-500 rounded-full" />
        </div>
      )}

      {error && (
        <div className="text-red-500 text-sm text-center py-10">
          Failed to load property insights.
        </div>
      )}

      {!isLoading && !error && property && (
        <div className="space-y-4">
          <div className="text-xs text-gray-500">View property insights</div>

          {/* IMAGE / VIDEO OVERVIEW */}
          <div className="w-full aspect-[6/4] overflow-hidden rounded-xl">
            {isVideoFile(property?.image_url?.[0]) ? (
              <video
                src={property?.image_url?.[0]}
                controls
                playsInline
                className="h-full w-full object-cover block"
              >
                Your browser does not support the video tag.
              </video>
            ) : (
              <img
                src={
                  property?.image_url?.[0] ||
                  "https://placehold.co/600x400?text=No+Image+Found"
                }
                alt="Property"
                className="h-full w-full object-cover block"
                onError={(e) => {
                  e.currentTarget.src =
                    "https://placehold.co/600x400?text=No+Image+Found";
                }}
              />
            )}
          </div>

          {/* TITLE */}
          <div className="text-lg font-semibold capitalize">
            {property?.listing_type === "land"
              ? `${property?.number_of_plots ?? ""}sqm Land`
              : `${property?.bedroom_count ?? ""} Bedroom ${property?.property_type ?? ""}`}{" "}
            for {property?.listing_type}
          </div>

          {/* META */}
          <div className="flex flex-col gap-1 text-sm text-gray-600">
            <div className="flex items-center gap-2">
              <IconUser size={14} />
              <span>
                Listed By:{" "}
                {`${property?.realtor?.first_name ?? ""} ${
                  property?.realtor?.last_name ?? ""
                }`}
              </span>
            </div>

            <div className="flex items-center gap-2">
              <IconMapPin size={14} />
              <span className="truncate">{property?.address}</span>
            </div>
          </div>

          {/* PRICE + DATE */}
          <div className="flex items-center gap-3 text-sm">
            <div className="flex items-center gap-1 text-gray-500">
              <IconCalendar size={14} />
              <span>
                {property?.created_at
                  ? new Date(property.created_at).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })
                  : "N/A"}
              </span>
            </div>

            <span className="text-gray-400">•</span>

            <div className="font-semibold text-blue-600">
              ₦{Number(property?.price || 0).toLocaleString()}
            </div>
          </div>
          {/* INSIGHTS */}
          <div className="bg-slate-100/90 rounded-lg p-4 border border-slate-200/60">
            <div className="text-sm font-semibold mb-3 text-slate-800">
              Property Insights
            </div>

            {/* Responsive grid: 2 columns on mobile, 4 columns on tablet/desktop */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {/* PAGE OPENS */}
              <div className="border border-slate-200 bg-white rounded-md p-3 flex flex-col items-center shadow-sm">
                <div className="h-9 w-9 rounded-full bg-cyan-50 flex items-center justify-center">
                  <IconEye size={18} className="text-cyan-600" />
                </div>
                <div className="text-xl font-bold mt-2 text-slate-800">
                  {property?.page_opened_count || 0}
                </div>
                <div className="text-xs text-slate-500 font-medium">
                  Page Opens
                </div>
              </div>

              {/* INSPECTIONS */}
              <div className="border border-slate-200 bg-white rounded-md p-3 flex flex-col items-center shadow-sm">
                <div className="h-9 w-9 rounded-full bg-emerald-50 flex items-center justify-center">
                  <IconHome size={18} className="text-emerald-600" />
                </div>
                <div className="text-xl font-bold mt-2 text-slate-800">
                  {property?.inspection_count || 0}
                </div>
                <div className="text-xs text-slate-500 font-medium">
                  Inspections
                </div>
              </div>

              {/* IMPRESSIONS */}
              <div className="border border-slate-200 bg-white rounded-md p-3 flex flex-col items-center shadow-sm">
                <div className="h-9 w-9 rounded-full bg-rose-50 flex items-center justify-center">
                  <IconSpeakerphone size={18} className="text-rose-600" />
                </div>
                <div className="text-xl font-bold mt-2 text-slate-800">
                  {property?.impressions_count || 0}
                </div>
                <div className="text-xs text-slate-500 font-medium">
                  Impressions
                </div>
              </div>

              {/* SAVES */}
              <div className="border border-slate-200 bg-white rounded-md p-3 flex flex-col items-center shadow-sm">
                {/* Updated background/icon color scheme to match a clean custom aesthetic */}
                <div className="h-9 w-9 rounded-full bg-pink-50 flex items-center justify-center">
                  <IconHeart size={18} className="text-pink-600" />
                </div>
                <div className="text-xl font-bold mt-2 text-slate-800">
                  {property?.save_count || 0}
                </div>
                {/* Corrected text node labeling */}
                <div className="text-xs text-slate-500 font-medium">Saves</div>
              </div>

              {/* WHATSAPP CONTACTS */}
              <div className="border border-slate-200 bg-white rounded-md p-3 flex flex-col items-center shadow-sm">
                <div className="h-9 w-9 rounded-full bg-emerald-50 flex items-center justify-center">
                  <IconBrandWhatsapp size={18} className="text-emerald-600" />
                </div>
                <div className="text-xl font-bold mt-2 text-slate-800">
                  {property?.whatsapp_contact_count || 0}
                </div>
                <div className="text-xs text-slate-500 font-medium">
                  WhatsApp Inquiries
                </div>
              </div>
            </div>
          </div>

          {/* FOOTER */}
          <div className="flex justify-end pt-4">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm border rounded-md hover:bg-gray-100"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default PropertyInsightModal;
